import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Row,
  Col,
  Button,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Alert,
  Spinner,
} from "reactstrap";
import { AvForm, AvField } from "availity-reactstrap-validation";
import { Tbody, Td, Th, Thead, Tr } from "react-super-responsive-table";
import FeatherIcon from "feather-icons-react";

import {
  updateApiCredentials,
  updateApiCredentialsClear,
} from "store/client/actions";
import TableLoader from "components/Common/Loader";
import { copyToClipboard } from "helpers/utils";
import { showSuccessNotification } from "store/actions";

function ApiCredentials() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { clientId } = useParams();

  // Redux state
  const { clientDetails } = useSelector((state) => state.clientReducer);
  const { loading, success, error } = useSelector(
    (state) => state.clientReducer.apiCredentials
  );

  // Local state
  const [modal, setModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [formData, setFormData] = useState({
    apiUrl: "",
    apiKey: "",
  });

  const copyApiKey = () => {
    copyToClipboard(clientDetails?.apiCredentials?.apiKey);
    dispatch(showSuccessNotification("API Key is copied to clipboard"));
  };

  // Toggle modal
  const toggleModal = () => {
    setModal(!modal);
    if (modal) {
      setEditMode(false);
      setShowApiKey(false);
      setFormData({ apiUrl: "", apiKey: "" });
    }
  };

  // Handle form submission
  const handleSubmit = (event, values) => {
    event.preventDefault();
    dispatch(
      updateApiCredentials({
        clientId,
        apiUrl: values.apiUrl,
        apiKey: values.apiKey,
      })
    );
  };

  // Handle edit
  const handleEdit = () => {
    if (
      clientDetails?.apiCredentials?.apiUrl ||
      clientDetails?.apiCredentials?.apiKey
    ) {
      setFormData({
        apiUrl: clientDetails?.apiCredentials?.apiUrl || "",
        apiKey: clientDetails?.apiCredentials?.apiKey || "",
      });
      setEditMode(true);
      setModal(true);
    }
  };

  // Handle add new
  const handleAddNew = () => {
    setFormData({ apiUrl: "", apiKey: "" });
    setEditMode(false);
    setShowApiKey(false);
    setModal(true);
  };

  // Effect to handle success and close modal
  useEffect(() => {
    if (success) {
      setModal(false);
      setEditMode(false);
      setShowApiKey(false);
      setFormData({ apiUrl: "", apiKey: "" });
      // Clear success after 3 seconds
      setTimeout(() => {
        dispatch(updateApiCredentialsClear());
      }, 3000);
    }
  }, [success, dispatch]);

  // Check if credentials exist
  const hasCredentials =
    clientDetails?.apiCredentials?.apiUrl ||
    clientDetails?.apiCredentials?.apiKey;

  return (
    <React.Fragment>
      <div className="container-fluid">
        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader>
                <CardTitle className="mb-0">
                  <h4 className="card-title">API Credentials Management</h4>
                </CardTitle>
              </CardHeader>
              <CardBody>
                {/* Action Buttons */}
                <div className="mb-3">
                  {!hasCredentials ? (
                    <Button
                      color="primary"
                      onClick={handleAddNew}
                      disabled={loading}
                    >
                      <i className="bx bx-plus me-1"></i>
                      Add API Credentials
                    </Button>
                  ) : (
                    <Button
                      color="info"
                      onClick={handleEdit}
                      disabled={loading}
                    >
                      <i className="bx bx-edit me-1"></i>
                      Edit API Credentials
                    </Button>
                  )}
                </div>

                {/* Credentials Table */}
                <div className="table-rep-plugin">
                  <div data-pattern="priority-columns">
                    <Table
                      id="api-credentials-table"
                      className="table table-striped"
                    >
                      <Thead className="text-center table-light">
                        <Tr>
                          <Th data-priority="1">Field</Th>
                          <Th data-priority="2">Value</Th>
                          <Th data-priority="3">Status</Th>
                        </Tr>
                      </Thead>
                      <Tbody className="text-center">
                        {loading && <TableLoader colSpan={3} />}
                        {!loading && (
                          <>
                            <Tr>
                              <Td>API URL</Td>
                              <Td>
                                {clientDetails?.apiCredentials?.apiUrl ? (
                                  <span
                                    className="text-truncate d-inline-block"
                                    style={{ maxWidth: "200px" }}
                                  >
                                    {clientDetails.apiCredentials?.apiUrl}
                                  </span>
                                ) : (
                                  <span className="text-muted">
                                    Not configured
                                  </span>
                                )}
                              </Td>
                              <Td>
                                {clientDetails?.apiCredentials?.apiUrl ? (
                                  <span className="badge badge-soft-success">
                                    Configured
                                  </span>
                                ) : (
                                  <span className="badge badge-soft-warning">
                                    Not Set
                                  </span>
                                )}
                              </Td>
                            </Tr>
                            <Tr>
                              <Td>API Key</Td>
                              <Td>
                                {clientDetails?.apiCredentials?.apiKey ? (
                                  <span>
                                    <span className="text-muted">
                                      ••••••••••••
                                      {clientDetails.apiCredentials?.apiKey?.slice(
                                        -4
                                      )}
                                    </span>
                                    <span className="cursor-pointer">
                                      <FeatherIcon
                                        icon="copy"
                                        onClick={copyApiKey}
                                        size={14}
                                      />
                                    </span>
                                  </span>
                                ) : (
                                  <span className="text-muted">
                                    Not configured
                                  </span>
                                )}
                              </Td>
                              <Td>
                                {clientDetails?.apiCredentials?.apiKey ? (
                                  <span className="badge badge-soft-success">
                                    Configured
                                  </span>
                                ) : (
                                  <span className="badge badge-soft-warning">
                                    Not Set
                                  </span>
                                )}
                              </Td>
                            </Tr>
                          </>
                        )}
                      </Tbody>
                    </Table>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Modal for Add/Edit API Credentials */}
      <Modal isOpen={modal} toggle={toggleModal} size="lg">
        <ModalHeader toggle={toggleModal}>
          {editMode ? "Edit API Credentials" : "Add API Credentials"}
        </ModalHeader>
        <AvForm onValidSubmit={handleSubmit}>
          <ModalBody>
            <Row>
              <Col md={12}>
                <AvField
                  name="apiUrl"
                  label="API URL *"
                  type="url"
                  placeholder="Enter API URL (e.g., https://api.example.com)"
                  value={formData.apiUrl}
                  onChange={(e) =>
                    setFormData({ ...formData, apiUrl: e.target.value })
                  }
                  validate={{
                    required: {
                      value: true,
                      errorMessage: "API URL is required",
                    },
                    pattern: {
                      value: "^https?://.+",
                      errorMessage:
                        "Please enter a valid URL starting with http:// or https://",
                    },
                  }}
                />
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <div className="position-relative">
                  <AvField
                    name="apiKey"
                    label="API Key *"
                    type={showApiKey ? "text" : "password"}
                    placeholder="Enter API Key"
                    value={formData.apiKey}
                    onChange={(e) =>
                      setFormData({ ...formData, apiKey: e.target.value })
                    }
                    validate={{
                      required: {
                        value: true,
                        errorMessage: "API Key is required",
                      },
                      minLength: {
                        value: 8,
                        errorMessage:
                          "API Key must be at least 8 characters long",
                      },
                    }}
                  />
                  <Button
                    type="button"
                    color="link"
                    className="position-absolute"
                    style={{
                      right: "10px",
                      top: "32px",
                      zIndex: 10,
                      padding: "0",
                      border: "none",
                      background: "transparent",
                    }}
                    onClick={() => setShowApiKey(!showApiKey)}
                  >
                    <FeatherIcon
                      icon={showApiKey ? "eye-off" : "eye"}
                      size={16}
                      className="text-muted"
                    />
                  </Button>
                </div>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <div className="alert alert-info">
                  <i className="bx bx-info-circle me-2"></i>
                  <strong>Security Note:</strong> API credentials are encrypted
                  and stored securely. Make sure to use strong, unique API keys
                  and only provide access to trusted endpoints.
                </div>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={toggleModal} disabled={loading}>
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={loading}>
              {loading && <Spinner size="sm" className="me-2" />}
              {editMode ? "Update Credentials" : "Save Credentials"}
            </Button>
          </ModalFooter>
        </AvForm>
      </Modal>
    </React.Fragment>
  );
}

export default ApiCredentials;
